import React, { useMemo } from 'react';
import { Tooltip } from '@mui/material';

import { toPixelRect } from './toPixelRect';
import type { ESignFieldPosition, ESignPageDimensions } from './types';

interface ESignFieldOverlayProps {
  position: ESignFieldPosition;
  pageDimensions: ESignPageDimensions;
  children: React.ReactNode;
  color: string;
  hoverContent?: React.ReactNode;
  isCheckbox?: boolean;
  isSelected?: boolean;
  style?: React.CSSProperties;
}

export function ESignFieldOverlay({
  position,
  pageDimensions,
  children,
  color,
  hoverContent,
  isCheckbox = false,
  isSelected = false,
  style
}: ESignFieldOverlayProps) {
  const pixelRect = useMemo(
    () => toPixelRect(position, pageDimensions),
    [position, pageDimensions]
  );
  const overlay = (
    <div
      style={{
        position: 'absolute',
        left: pixelRect.left,
        top: pixelRect.top,
        width: pixelRect.width,
        height: pixelRect.height,
        pointerEvents: 'auto',
        border: `2px solid ${color}`,
        borderRadius: isCheckbox ? 0 : 2,
        backgroundColor: isCheckbox ? 'transparent' : `${color}30`,
        outline: isSelected ? `2px solid ${color}` : undefined,
        outlineOffset: 2,
        display: 'flex',
        alignItems: 'center',
        overflow: 'hidden',
        ...style
      }}
    >
      {children}
    </div>
  );

  if (!hoverContent) {
    return overlay;
  }

  return (
    <Tooltip
      arrow
      disableInteractive
      placement="top"
      title={hoverContent}
      slotProps={{
        tooltip: {
          sx: {
            bgcolor: '#364955',
            borderRadius: '8px',
            boxShadow: '0 4px 12px rgba(10, 30, 40, 0.24)',
            fontSize: 12,
            fontWeight: 500,
            px: 1.5,
            py: 1
          }
        },
        arrow: {
          sx: {
            color: '#364955'
          }
        }
      }}
    >
      {overlay}
    </Tooltip>
  );
}
